import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setDemoClientId, SANDTON_DEMO_CLIENT_ID } from "@/lib/demoMode";
import Dashboard from "./Dashboard";
import DemoOnboarding from "@/components/landing/DemoOnboarding";
import PostOnboardingLeadCapture from "@/components/landing/PostOnboardingLeadCapture";

const DemoActivationCard = () => {
  const navigate = useNavigate();
  const [sidebarHidden, setSidebarHidden] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => setSidebarHidden(!!(e as CustomEvent).detail);
    window.addEventListener("orbit:sidebar-hidden", handler);
    return () => window.removeEventListener("orbit:sidebar-hidden", handler);
  }, []);

  return (
    <div
      className={`mb-3 sm:mb-5 transition-[margin] duration-700 ease-in-out ${sidebarHidden ? "" : ""}`}
    >
      <div
        className="relative overflow-hidden rounded-2xl px-4 sm:px-7 py-3.5 sm:py-5"
        style={{
          background:
            "linear-gradient(180deg, rgba(16,22,34,0.85) 0%, rgba(10,14,22,0.9) 100%)",
          backdropFilter: "blur(18px) saturate(140%)",
          WebkitBackdropFilter: "blur(18px) saturate(140%)",
          border: "1px solid rgba(255,255,255,0.07)",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.04) inset, 0 20px 50px -20px rgba(0,0,0,0.6), 0 0 60px -30px rgba(0,229,137,0.25)",
        }}
      >
        {/* Ambient accent glows */}
        <div
          className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full opacity-50"
          style={{
            background:
              "radial-gradient(circle, rgba(0,229,137,0.18) 0%, transparent 60%)",
            filter: "blur(20px)",
          }}
        />
        <div
          className="pointer-events-none absolute -bottom-24 right-1/3 h-64 w-64 rounded-full opacity-40"
          style={{
            background:
              "radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 60%)",
            filter: "blur(20px)",
          }}
        />
        {/* Top hairline */}
        <div
          className="pointer-events-none absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(0,229,137,0.4), rgba(139,92,246,0.4), transparent)",
          }}
        />

        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
          <div className="min-w-0 flex-1">
            {/* Live demo indicator */}
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-[rgba(0,229,137,0.25)] bg-[rgba(0,229,137,0.07)] px-2.5 py-0.5">
              <span className="relative flex h-[7px] w-[7px]">
                <span
                  className="absolute inline-flex h-full w-full rounded-full bg-[#00FF88] opacity-60"
                  style={{ animation: "ping 2.4s cubic-bezier(0,0,0.2,1) infinite" }}
                />
                <span
                  className="relative inline-flex h-[7px] w-[7px] rounded-full bg-[#00FF88]"
                  style={{ boxShadow: "0 0 8px 2px rgba(0,255,136,0.55)" }}
                />
              </span>
              <span
                className="text-[10.5px] font-semibold tracking-[0.08em] uppercase"
                style={{ color: "#00E589", textShadow: "0 0 10px rgba(0,229,137,0.4)" }}
              >
                Live Demo
              </span>
            </div>

            <h2
              className="text-[18px] sm:text-[22px] md:text-[24px] font-bold leading-tight tracking-[-0.015em] text-white"
            >
              You're in OrbitRank{" "}
              <span style={{ color: "#FF6B6B" }}>Demo Workspace</span>
            </h2>
            <p
              className="mt-1 text-[12px] sm:text-[13px] leading-snug max-w-[640px]"
              style={{ color: "rgba(229,231,235,0.78)" }}
            >
              This is a live demo. See how AI can grow your business with SEO, AI
              Search &amp; Google Ads — all in one intelligent platform.
            </p>
          </div>

          <div className="flex flex-row items-center justify-between md:flex-col md:items-end gap-2 md:gap-1.5 shrink-0">
            <button
              onClick={() => navigate("/pricing")}
              className="rounded-xl px-4 sm:px-5 py-2 sm:py-2.5 text-[12.5px] sm:text-[13.5px] font-semibold whitespace-nowrap transition-all duration-200"
              style={{
                background: "linear-gradient(135deg, #00E589 0%, #00B86B 100%)",
                color: "#052e16",
                boxShadow:
                  "0 0 22px rgba(0,229,137,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
                letterSpacing: "-0.01em",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 0 32px rgba(0,229,137,0.55), inset 0 1px 0 rgba(255,255,255,0.32)";
                e.currentTarget.style.filter = "brightness(1.06)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 0 22px rgba(0,229,137,0.35), inset 0 1px 0 rgba(255,255,255,0.25)";
                e.currentTarget.style.filter = "brightness(1)";
              }}
            >
              Start Your Free Trial →
            </button>
            <div
              className="flex items-center gap-1.5 text-[10.5px] sm:text-[11px]"
              style={{ color: "rgba(229,231,235,0.55)" }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              No credit card required
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DemoDashboard = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setDemoClientId(SANDTON_DEMO_CLIENT_ID);
    setReady(true);
    return () => setDemoClientId(null);
  }, []);
  if (!ready) return <div className="min-h-screen bg-background" />;
  return (
    <>
      <Dashboard headerSlot={<DemoActivationCard />} />
      <DemoOnboarding />
      <PostOnboardingLeadCapture />
    </>
  );
};

export default DemoDashboard;
